import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Plus, X, Trash, Lock } from "@phosphor-icons/react";

const STAGE_LABELS = {
  pre_filing: "Pre-Filing", edp_mediation: "EDP Mediation", filed: "Filed",
  hearing_scheduled: "Hearing Scheduled", judgment_landlord: "Judgment — Landlord",
  judgment_tenant: "Judgment — Tenant", appeal: "Appeal",
  writ_of_possession: "Writ of Possession", holdover: "Holdover",
  deferred: "Deferred", tfa: "TFA", lockout_scheduled: "Lockout Scheduled",
  resolved: "Resolved", dismissed: "Dismissed",
};
const money = (c) => `$${((c || 0) / 100).toLocaleString()}`;

export default function Evictions() {
  const [cases, setCases] = useState([]);
  const [summary, setSummary] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [stages, setStages] = useState([]);
  const [show, setShow] = useState(false);
  const load = async () => {
    try {
      const r = await api.get("/office/evictions");
      setCases(r.data.cases || []);
      setSummary(r.data.summary);
      setStages(r.data.stages || []);
      setCanEdit(!!r.data.can_edit);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Close eviction case?")) return;
    try { await api.delete(`/office/evictions/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Failed"); }
  };
  const patchStage = async (id, stage) => {
    try { await api.patch(`/office/evictions/${id}`, { stage }); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="evictions-page">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
            <Gavel weight="fill" className="text-red-600"/> Evictions & Holdovers
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Track EDP mediation, filings, court dates, TFA / holdovers, deferred cases, and rental-aid coordination. Court dates auto-post to the Team Calendar.
          </p>
        </div>

        {!canEdit && (
          <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-[11px] flex items-center gap-2">
            <Lock size={14}/> Read-only — owner-level access required to edit cases.
          </div>
        )}

        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[
              ["Open", summary.open, "bg-slate-900"],
              ["Court Dates", summary.upcoming_court, "bg-red-600"],
              ["Holdovers", summary.holdovers, "bg-amber-600"],
              ["Deferred", summary.deferred, "bg-cyan-600"],
              ["Amount Owed", money(summary.total_owed_cents), "bg-emerald-700"],
            ].map(([l, v, bg]) => (
              <div key={l} className={`p-3 ${bg} text-white shadow-[3px_3px_0_#0f172a]`}>
                <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{l}</div>
                <div className="text-2xl font-black">{v}</div>
              </div>
            ))}
          </div>
        )}

        {canEdit && (
          <button onClick={() => setShow(true)} data-testid="new-eviction-btn"
            className="h-10 px-3 bg-red-600 text-white font-black uppercase tracking-widest text-[11px] shadow-[3px_3px_0_#0f172a] inline-flex items-center gap-1">
            <Plus size={14}/> New Case
          </button>
        )}

        {cases.length === 0 ? (
          <div className="rugged-card p-6 text-center text-sm text-slate-500">No cases.</div>
        ) : (
          <div className="space-y-2">
            {cases.map(c => (
              <div key={c.id} className="p-3 rugged-card space-y-1" data-testid={`case-${c.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-black text-sm">{c.tenant_name}</div>
                      <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${
                        c.status === "closed" ? "bg-slate-400 text-white" : "bg-red-600 text-white"}`}>
                        {STAGE_LABELS[c.stage] || c.stage}
                      </span>
                      {c.is_holdover && <span className="px-2 h-6 leading-6 bg-amber-500 text-white font-black uppercase tracking-widest text-[9px]">Holdover</span>}
                      {c.is_deferred && <span className="px-2 h-6 leading-6 bg-cyan-500 text-white font-black uppercase tracking-widest text-[9px]">Deferred</span>}
                    </div>
                    <div className="text-[10px] text-slate-600">{c.property_address}{c.unit && ` · ${c.unit}`} · {c.landlord_name || "no landlord"}</div>
                    <div className="text-[10px] mt-1">
                      Owed: <b>{money(c.amount_owed_cents)}</b>{c.months_behind > 0 && ` · ${c.months_behind}mo behind`}
                      {c.next_court_date && <> · <b className="text-red-700">Court {c.next_court_date}</b></>}
                      {c.edp_meeting_date && <> · EDP {c.edp_meeting_date}</>}
                      {c.move_out_deadline && <> · Move-out {c.move_out_deadline}</>}
                      {c.docket_number && ` · #${c.docket_number}`}
                    </div>
                    {c.rental_assistance_program !== "none" && (
                      <div className="text-[10px] text-emerald-700">
                        Aid: {c.rental_assistance_program.toUpperCase()} {c.rental_assistance_status && `— ${c.rental_assistance_status}`}
                      </div>
                    )}
                    {c.notes && <div className="text-[11px] text-slate-700 mt-1">{c.notes}</div>}
                  </div>
                  {canEdit && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <select value={c.stage} onChange={e => patchStage(c.id, e.target.value)} data-testid={`case-stage-${c.id}`}
                        className="h-8 px-2 border-2 border-slate-300 text-[10px] font-black uppercase">
                        {stages.map(s => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
                      </select>
                      <button onClick={() => del(c.id)} className="h-8 w-8 border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                        <Trash size={14}/>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {show && <EvictionModal stages={stages} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }}/>}
      </div>
    </AdminShell>
  );
}

function EvictionModal({ stages, onClose, onSaved }) {
  const [f, setF] = useState({
    tenant_name: "", tenant_phone: "", tenant_email: "",
    property_address: "", unit: "", landlord_name: "",
    stage: "pre_filing", status: "open",
    amount_owed_cents: 0, months_behind: 0,
    filing_date: "", edp_meeting_date: "", next_court_date: "",
    docket_number: "", rental_assistance_program: "none",
    rental_assistance_status: "", move_out_deadline: "",
    attorney_name: "", notes: "",
    is_holdover: false, is_deferred: false,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.tenant_name || !f.property_address) return toast.error("Tenant + address required");
    setBusy(true);
    try {
      await api.post("/office/evictions", {
        ...f,
        amount_owed_cents: Math.round((parseFloat(f.amount_owed_cents) || 0) * 100),
        months_behind: parseInt(f.months_behind) || 0,
      });
      toast.success("Case opened"); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-[80] flex items-center justify-center p-4 overflow-y-auto" data-testid="eviction-modal">
      <div className="bg-white w-full max-w-2xl border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] my-4">
        <div className="p-3 bg-red-600 text-white flex items-start justify-between sticky top-0">
          <div className="font-black uppercase tracking-widest">New Eviction Case</div>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-4 space-y-2 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-2">
            <input value={f.tenant_name} onChange={e => setF({ ...f, tenant_name: e.target.value })} placeholder="Tenant name *" data-testid="ev-tenant" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={f.tenant_phone} onChange={e => setF({ ...f, tenant_phone: e.target.value })} placeholder="Phone" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <input list="known-addresses" autoComplete="off" value={f.property_address} onChange={e => setF({ ...f, property_address: e.target.value })} placeholder="Property address *" data-testid="ev-addr" className="col-span-2 h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })} placeholder="Unit" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <input value={f.landlord_name} onChange={e => setF({ ...f, landlord_name: e.target.value })} placeholder="Landlord name" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <div className="grid grid-cols-2 gap-2">
            <select value={f.stage} onChange={e => setF({ ...f, stage: e.target.value })} className="h-10 px-3 border-2 border-slate-300 text-sm">
              {stages.map(s => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
            </select>
            <input value={f.docket_number} onChange={e => setF({ ...f, docket_number: e.target.value })} placeholder="Docket #" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" value={f.amount_owed_cents} onChange={e => setF({ ...f, amount_owed_cents: e.target.value })} placeholder="Amount owed ($)" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input type="number" value={f.months_behind} onChange={e => setF({ ...f, months_behind: e.target.value })} placeholder="Months behind" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Filing<input value={f.filing_date} onChange={e => setF({ ...f, filing_date: e.target.value })} placeholder="YYYY-MM-DD" className="h-9 px-2 border-2 border-slate-300 text-xs"/></label>
            <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">EDP Meeting<input value={f.edp_meeting_date} onChange={e => setF({ ...f, edp_meeting_date: e.target.value })} placeholder="YYYY-MM-DD" className="h-9 px-2 border-2 border-slate-300 text-xs"/></label>
            <label className="text-[9px] uppercase tracking-widest text-slate-500 flex flex-col gap-1">Next Court<input value={f.next_court_date} onChange={e => setF({ ...f, next_court_date: e.target.value })} placeholder="YYYY-MM-DD" data-testid="ev-court" className="h-9 px-2 border-2 border-slate-300 text-xs"/></label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={f.move_out_deadline} onChange={e => setF({ ...f, move_out_deadline: e.target.value })} placeholder="Move-out deadline" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
            <input value={f.attorney_name} onChange={e => setF({ ...f, attorney_name: e.target.value })} placeholder="Attorney" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={f.rental_assistance_program} onChange={e => setF({ ...f, rental_assistance_program: e.target.value })} className="h-10 px-3 border-2 border-slate-300 text-sm">
              {["none", "tap", "erap", "phdc", "other"].map(p => <option key={p} value={p}>{p === "none" ? "No rental aid" : p.toUpperCase()}</option>)}
            </select>
            <input value={f.rental_assistance_status} onChange={e => setF({ ...f, rental_assistance_status: e.target.value })} placeholder="Aid status / notes" className="h-10 px-3 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="flex gap-4">
            <label className="text-xs inline-flex items-center gap-2"><input type="checkbox" checked={f.is_holdover} onChange={e => setF({ ...f, is_holdover: e.target.checked })}/> Holdover tenant</label>
            <label className="text-xs inline-flex items-center gap-2"><input type="checkbox" checked={f.is_deferred} onChange={e => setF({ ...f, is_deferred: e.target.checked })}/> Deferred</label>
          </div>
          <textarea value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} rows={3} placeholder="Notes" className="w-full p-2 border-2 border-slate-300 text-sm"/>
          <button onClick={save} disabled={busy || !f.tenant_name || !f.property_address} data-testid="ev-save"
            className="w-full h-11 bg-red-600 text-white font-black uppercase tracking-widest text-sm disabled:opacity-50 shadow-[3px_3px_0_#0f172a]">
            {busy ? "Saving…" : "Open Case"}
          </button>
        </div>
      </div>
    </div>
  );
}
